import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IFormType } from "../../model/formconfiguration/FormType";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import CreateFormTypeInteractor from "../../interactor/formconfiguration/CreateFormTypeInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetFormTypesInteractor from "../../interactor/formconfiguration/GetFormTypesInteractor";
import UpdateFormTypeInteractor from "../../interactor/formconfiguration/UpdateFormTypeInteractor";
import CopyFormTypeInteractor from "../../interactor/formconfiguration/CopyFormTypeInteractor";
import { IEvaluationType } from "../../model/evaluation/EvaluationType";
import JoinEvaluationTypeToFormTypeInteractor from "../../interactor/formconfiguration/JoinEvaluationTypeToFormTypeInteractor";
import RemoveEvaluationTypeFromFormTypeInteractor from "../../interactor/formconfiguration/RemoveEvaluationTypeFromFormTypeInteractor";
import { IFormTypeEvaluationType } from "../../model/formconfiguration/FormTypeEvaluationType";
import GetFormTypeEvaluationsInteractor from "../../interactor/formconfiguration/GetFormTypeEvaluationsInteractor";
import CreateNewFormVersionInteractor from "../../interactor/formconfiguration/CreateNewFormVersionInteractor";
import PublishFormVersionInteractor from "../../interactor/formconfiguration/PublishFormVersionInteractor";
import CancelFormVersionInteractor from "../../interactor/formconfiguration/CancelFormVersionInteractor";

export interface TFormTypesPresenter extends TLoadingAwarePresenter {
  formTypes: IFormType[];
  copyFormModalVisible?: boolean;
  rowData?: any;
  evaluationTypes?: IEvaluationTypesPresentationModel[];
  currentFormType?: IFormType;
  assignedEvaluationTypeIds?: number[];
  formTypeEvaluationTypes?: IFormTypeEvaluationType[];
  newFromVersionModalVisible?: boolean;
  publishFormVersionDialogVisible?: boolean;
  cancelFormVersionDialogVisible?: boolean;
}

export interface IFormTypesPresenter extends TFormTypesPresenter, TPresentable {
  setFormTypes(formTypes: IFormType[]): void;
  setEvaluationTypes(evaluationTypes: IEvaluationType[]);
  createFormType(formType: IFormType): void;
  updateFormType(formType: IFormType): void;
  copyFormType(formTypeCode: string, newFormType: IFormType): void;
  showCopyFormModal(): void;
  hideCopyFormModal(): void;
  setRowData(rowData: any): void;
  redirect(route: any): any;
  onRowExpanded(expanded: any, record: any): void;
  onEvaluationTypeSelectChange(selectedEvaluationTypeIds: number[]): void;
  createNewFormVersion(formTypeCode: string, formType: IFormType): void;
  publishFormVersion(formTypeCode: string): void;
  cancelFormVersion(formTypeCode: string): void;
  showNewFormVersionModal(): void;
  hideNewFormVersionModal(): void;
  showPublishFormVesionDialog(): void;
  hidePublishFormVesionDialog(): void;
  showCancelFormVersionDialog(): void;
  hideCancelFormVersionDialog(): void;
}
export interface IEvaluationTypesPresentationModel extends IEvaluationType {
  label: string;
  value: number;
}
const defaultState: TFormTypesPresenter = {
  formTypes: []
};
const EvaluationTypesPresentationModelMapper = (
  evaluationTypes: IEvaluationType[]
): IEvaluationTypesPresentationModel[] => {
  return evaluationTypes.map((evaluationType) => {
    return {
      ...evaluationType,
      value: evaluationType.id,
      label: evaluationType.name
    };
  });
};
const AssignedEvaluationTypesMapper = (
  formTypeEvaluationTypes: IFormTypeEvaluationType[]
): number[] => {
  return formTypeEvaluationTypes?.map((formTypeEvaluationType) => {
    return formTypeEvaluationType.evaluationTypeId;
  });
};

const FormTypesPresenter = withStore<IFormTypesPresenter, TFormTypesPresenter>(
  ({ application, store, loader, translate }): IFormTypesPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TFormTypesPresenter>();

    loader.start("formTypesLoader");

    const setFormTypes = (formTypes: IFormType[]) => {
      _store.update({ formTypes });
      loader.stop("formTypesLoader");
    };

    const getFormTypes = async () => {
      try {
        let formTypes = await _application.container
          .resolve<GetFormTypesInteractor>("getFormTypes")
          .execute();
        _store.update({ formTypes });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const createFormType = async (formType: IFormType) => {
      try {
        await _application.container
          .resolve<CreateFormTypeInteractor>("createFormType")
          .execute(formType);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("formType_form.messages.create_formType_success"));
        await getFormTypes();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const updateFormType = async (formType: IFormType) => {
      try {
        await _application.container
          .resolve<UpdateFormTypeInteractor>("updateFormType")
          .execute(formType);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("formType_form.messages.update_formType_success"));
        await getFormTypes();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };
    const showCopyFormModal = () => {
      _store.update({ copyFormModalVisible: true });
    };

    const hideCopyFormModal = () => {
      _store.update({ copyFormModalVisible: false });
    };

    const setRowData = (rowData: any) => {
      _store.update({ rowData });
    };

    const setEvaluationTypes = (evaluationTypes: IEvaluationType[]) => {
      evaluationTypes = EvaluationTypesPresentationModelMapper(evaluationTypes);
      _store.update({ evaluationTypes });
    };

    const copyFormType = async (
      formTypeCode: string,
      newFormType: IFormType
    ) => {
      try {
        await _application.container
          .resolve<CopyFormTypeInteractor>("copyFormType")
          .execute(formTypeCode, newFormType);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("formType_form.messages.copy_formType_success"));
        await getFormTypes();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };
    const getFormTypeEvaluationTypes = async (formTypeCode: string) => {
      try {
        let formTypeEvaluationTypes = await _application.container
          .resolve<GetFormTypeEvaluationsInteractor>("getFormTypeEvaluations")
          .execute(formTypeCode);
        let assignedEvaluationTypeIds = AssignedEvaluationTypesMapper(
          formTypeEvaluationTypes
        );
        _store.update({ assignedEvaluationTypeIds, formTypeEvaluationTypes });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };
    const joinEvaluationTypeToFormType = async (
      formTypeCode: string,
      evaluationTypeId: number
    ) => {
      try {
        await _application.container
          .resolve<JoinEvaluationTypeToFormTypeInteractor>(
            "joinEvaluationTypeToFormType"
          )
          .execute(formTypeCode, evaluationTypeId);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("formType_form.messages.join_evaluationType_success")
          );
        await getFormTypeEvaluationTypes(formTypeCode);
        loader.stop("evaluationTypeLoader");
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };
    const removeEvaluationTypeFromFormType = async (
      formTypeEvaluationTypeId: number
    ) => {
      try {
        let { currentFormType } = _store.getState();
        await _application.container
          .resolve<RemoveEvaluationTypeFromFormTypeInteractor>(
            "removeEvaluationTypeFromFormType"
          )
          .execute(formTypeEvaluationTypeId);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("formType_form.messages.remove_evaluationType_success")
          );
        await getFormTypeEvaluationTypes(currentFormType.code);
        loader.stop("evaluationTypeLoader");
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };
    const onEvaluationTypeSelectChange = async (
      selectedEvaluationTypeIds: number[]
    ) => {
      let {
        assignedEvaluationTypeIds,
        currentFormType,
        formTypeEvaluationTypes
      } = _store.getState();
      loader.start("evaluationTypeLoader");
      if (selectedEvaluationTypeIds.length > assignedEvaluationTypeIds.length) {
        let newEvaluationTypeIds = selectedEvaluationTypeIds.filter(
          (selectedRuleGroupsId) =>
            !assignedEvaluationTypeIds.includes(selectedRuleGroupsId)
        );
        newEvaluationTypeIds.forEach(
          async (evaluationTypeId) =>
            await joinEvaluationTypeToFormType(
              currentFormType.code,
              evaluationTypeId
            )
        );
      } else if (
        selectedEvaluationTypeIds.length < assignedEvaluationTypeIds.length
      ) {
        let removedEvaluationTypeIds = assignedEvaluationTypeIds.filter(
          (assignedRuleGroupId) =>
            !selectedEvaluationTypeIds.includes(assignedRuleGroupId)
        );
        removedEvaluationTypeIds.forEach(async (evaluationTypeId) => {
          let formTypeEvaluationType: IFormTypeEvaluationType =
            formTypeEvaluationTypes.find(
              (item) => item.evaluationTypeId === evaluationTypeId
            );
          await removeEvaluationTypeFromFormType(formTypeEvaluationType.id);
        });
      }
      _store.update({ assignedEvaluationTypeIds });
    };
    const setExpandedRowData = async (formType: IFormType) => {
      loader.start("evaluationTypeLoader");
      _store.update({
        currentFormType: formType
      });
      await getFormTypeEvaluationTypes(formType.code);
      loader.stop("evaluationTypeLoader");
    };
    const onRowExpanded = async (expanded: any, record: any) => {
      if (expanded) {
        await setExpandedRowData(record);
      }
    };

    const createNewFormVersion = async (
      formTypeCode: string,
      formType: IFormType
    ) => {
      try {
        loader.start("formTypesLoader");

        await _application.container
          .resolve<CreateNewFormVersionInteractor>("createNewFormVersion")
          .execute(formTypeCode, formType);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("formTypes_page.new_version_success_msg"));

        await getFormTypes();

        loader.stop("formTypesLoader");
      } catch (error) {
        loader.stop("formTypesLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const publishFormVersion = async (formTypeCode: string) => {
      try {
        loader.start("formTypesLoader");

        await _application.container
          .resolve<PublishFormVersionInteractor>("publishFormVersion")
          .execute(formTypeCode);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("formTypes_page.publish_success_msg"));

        await getFormTypes();

        loader.stop("formTypesLoader");
      } catch (error) {
        loader.stop("formTypesLoader");

        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const cancelFormVersion = async (formTypeCode: string) => {
      try {
        loader.start("formTypesLoader");

        await _application.container
          .resolve<CancelFormVersionInteractor>("cancelFormVersion")
          .execute(formTypeCode);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("formTypes_page.cancel_success_msg"));

        await getFormTypes();

        loader.stop("formTypesLoader");
      } catch (error) {
        loader.stop("formTypesLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute(formTypeCode);
      }
    };

    const showNewFormVersionModal = () => {
      _store.update({ newFromVersionModalVisible: true });
    };

    const hideNewFormVersionModal = () => {
      _store.update({ newFromVersionModalVisible: false });
    };

    const showPublishFormVesionDialog = () => {
      _store.update({ publishFormVersionDialogVisible: true });
    };

    const hidePublishFormVesionDialog = () => {
      _store.update({ publishFormVersionDialogVisible: false });
    };

    const showCancelFormVersionDialog = () => {
      _store.update({ cancelFormVersionDialogVisible: true });
    };

    const hideCancelFormVersionDialog = () => {
      _store.update({ cancelFormVersionDialogVisible: false });
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setFormTypes,
      createFormType,
      updateFormType,
      copyFormType,
      showCopyFormModal,
      hideCopyFormModal,
      setRowData,
      redirect: _application.navigator.push,
      setEvaluationTypes,
      onEvaluationTypeSelectChange,
      onRowExpanded,
      createNewFormVersion,
      publishFormVersion,
      cancelFormVersion,
      showNewFormVersionModal,
      hideNewFormVersionModal,
      showPublishFormVesionDialog,
      hidePublishFormVesionDialog,
      showCancelFormVersionDialog,
      hideCancelFormVersionDialog
    };
  },
  defaultState
);

export default FormTypesPresenter;
